import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, linkedSignal, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';

import {
  DiagnosisChipView,
  DrugModel,
  DrugScheduleModel,
  DrugScheduleWriteModel,
  GuaranteeDecision,
  MealRelation,
  PackageCoverageMatchService,
  PackageCoverageMatchSubOptionModel,
  PrescriptionBaselinesService,
  PrescriptionDetailsKind,
  PrescriptionDetailsWriteModel,
  PrescriptionPackageCoverageModel,
  PrescriptionPackageCoverageWriteModel,
  PrescriptionRestrictionModel,
  PrescriptionWriteModel,
  ScheduleTimeUnit,
  ServiceKind,
  V2PrescriptionCategoryModel,
  V2PrescriptionModel,
  codingOf,
  endOfDayIso,
  isDrugsDetails,
  isDrugsWriteDetails,
  startOfDayIso,
  toDateInput,
} from '@lib/guarantees/data-access';
import { addPrescription, updatePrescription } from '@lib/guarantees/data-access/store/guarantees-v2.actions';
import { selectLiveRefreshContext } from '@lib/guarantees/data-access/store/guarantees-v2.selectors';
import {
  DECISION_ICON,
  DECISION_LABEL_KEY,
  MEAL_RELATION_LABEL_KEY,
  SERVICE_KIND_ICON,
  SERVICE_KIND_LABEL_KEY,
  TIME_UNIT_LABEL_KEY,
} from '@lib/guarantees/feature/components/common/guarantee-vocabulary';
import {
  DECISION_ANSWERS,
  DECISION_BLOCKER_HINT_KEY,
  DecisionAnswer,
  DecisionBlocker,
  DecisionDraft,
  allowsGrant,
  hasCategories,
  toDecisionWriteModel,
} from '@lib/guarantees/feature/components/common/prescription-decisions';
import { partnerServiceFor } from '@lib/guarantees/feature/components/common/prescription-fulfillment';
import { isPrescriptionUnmapped } from '@lib/guarantees/feature/components/common/prescription-mapping';
import { ServicePickerComponent, ServiceSelection } from '@lib/guarantees/feature/components/service-picker/service-picker.component';
import { EDIT_REASONS } from '@lib/prescriptions/data-access';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { of, take } from 'rxjs';

import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

import { CategoryPickerComponent } from '../components/common/category-picker.component';
import { CheckBoxComponent } from '../components/common/check-box.component';
import { ControlDirective } from '../components/common/control.directive';
import { FieldLabelComponent } from '../components/common/field-label.component';
import { OptionPickerComponent, PickerOption } from '../components/common/option-picker.component';
import {
  PackageOptionPickerComponent,
  PackageOptionSelection,
  PackageOptionsStatus,
  franchiseLimit,
  selectionFromSnapshot,
  selectionFromWriteModel,
  toCoverageWriteModel,
} from '../components/common/package-option-picker.component';
import { SpinnerComponent } from '../components/common/spinner.component';
import { SEGMENTED_GROUP_CLASS, ToggleDirective } from '../components/common/toggle.directive';
import { DIALOG_CLASSES, FIELD_MESSAGE_CLASS } from '../components/ui/ui.classes';
import { DECLINE_REASONS, GUARANTEE_REASONS } from './decision-reasons';
import { ProviderRestrictionComponent } from './provider-restriction.component';

export interface PrescriptionFormData {
  /**
   * `edit` amends a written line, which has a history to explain itself to and so is asked why.
   * `draft` amends one that has not been sent yet — the same record, without a record's history.
   */
  mode: 'create' | 'edit' | 'draft';
  /** The line as it stands, in the shape the form writes it back in. */
  initial?: PrescriptionWriteModel;
  /** The diagnoses the guarantee carries — the only ones a line may be pointed at. */
  diagnoses?: DiagnosisChipView[];
  /**
   * When the guarantee was raised — the day whose package content the options are matched in.
   * Unstated means the guarantee is being created right now.
   */
  guaranteeCreatedAt?: string;
  /**
   * Ask for the ruling here, in the same pass as the line. Off while amending a written line: that
   * one already carries a ruling, which the amendment restates as it stands — re-answering it is
   * the ruling flow's act rather than this form's, since it has withdrawal rules this form does
   * not read.
   */
  collectsDecision?: boolean;
  /** The ruling as it stands, where one is already being given to a queued line. */
  decision?: DecisionDraft;
  /** The package options a written line is guaranteed under, as stored on it — the choice an amendment starts from. */
  coverage?: PrescriptionPackageCoverageModel;
  /** The member (by user id) whose package options are offered. */
  memberUserId?: string | null;
}

export interface PrescriptionFormResult {
  /**
   * Carries `diagnosisId`: attaching a diagnosis is part of the write, not a second call. A
   * `details.quantity` above one is a course — that many rows under one group key, each its own
   * line. On an amendment it carries the ruling the line already had, restated as it stands.
   */
  prescription: PrescriptionWriteModel;
  /** Always stated on an amendment — the line's history reads by why it changed. */
  editReason?: string;
  /** Detaching only. The write model cannot say it — an unstated diagnosis means "leave it alone". */
  clearDiagnosis?: boolean;
  /**
   * The ruling given alongside a new line, still a draft: the create-guarantee queue holds it to
   * rule on again before the record exists. Unset leaves the line undecided, where every line starts.
   */
  decision?: DecisionDraft;
}

/**
 * An amendment REPLACES what it states, so every field is carried through here — including the ones
 * this form never shows. `considerPaid` is omitted on purpose: unstated keeps whatever the payment
 * side recorded, and this form has no business overwriting it. The ruling is restated as it stands:
 * the service refuses an amendment without one, and keeps one restated unchanged as it was made.
 */
export function toPrescriptionWriteModel(prescription: V2PrescriptionModel): PrescriptionWriteModel {
  return {
    kind: prescription.kind,
    code: prescription.code,
    name: prescription.name,
    isFreeForm: prescription.isFreeForm,
    diagnosisId: prescription.diagnosisId,
    categories: prescription.categories,
    completedInAppointment: prescription.completedInAppointment,
    completionReason: prescription.completionReason,
    details: toDetailsWriteModel(prescription.details),
    restriction: prescription.restriction,
    financials: {
      reserved: prescription.financials?.reserved,
      coverageFranchise: prescription.financials?.coverageFranchise,
    },
    decision: {
      decision: prescription.decision.decision,
      reason: prescription.decision.reason,
      note: prescription.decision.note,
    },
  };
}

/**
 * Wire shape: products go back as `drugCodes`, and the service re-resolves them from the dictionary.
 * `_dt` is written first — the backend's polymorphic reader only accepts a leading discriminator.
 */
function toDetailsWriteModel(details: V2PrescriptionModel['details']): PrescriptionDetailsWriteModel | undefined {
  if (!details) {
    return undefined;
  }

  const base = { quantity: details.quantity, startDate: details.startDate, endDate: details.endDate };

  if (!isDrugsDetails(details)) {
    return { _dt: details._dt, ...base };
  }

  return {
    _dt: PrescriptionDetailsKind.Drugs,
    ...base,
    protocolIdentifier: details.protocolIdentifier,
    form: details.form,
    requiredQuantity: details.requiredQuantity,
    treatmentPackages: details.treatmentPackages,
    comment: details.comment,
    drugCodes: details.drugs?.map(drug => drug.code).filter((code): code is string => !!code),
    schedule: toScheduleWriteModel(details.schedule),
  };
}

/** `dailyDose`/`courseDose` are server-derived — sending them back would be a second opinion. */
function toScheduleWriteModel(schedule: DrugScheduleModel | undefined): DrugScheduleWriteModel | undefined {
  if (!schedule) {
    return undefined;
  }

  return {
    dose: schedule.dose,
    doseUnit: schedule.doseUnit,
    frequency: schedule.frequency,
    period: schedule.period,
    periodUnit: schedule.periodUnit,
    duration: schedule.duration,
    durationUnit: schedule.durationUnit,
    mealRelation: schedule.mealRelation,
    timesOfDay: schedule.timesOfDay,
    asNeeded: schedule.asNeeded,
  };
}

/**
 * How the form is presented, wherever it is opened from — writing a line onto a guarantee, or
 * collecting one inside the create-guarantee flow. Stated once so the two never drift apart.
 */
export const PRESCRIPTION_FORM_DIALOG_CONFIG = {
  width: '90vw',
  maxWidth: '560px',
  maxHeight: '90vh',
  autoFocus: false,
  // Named by the heading it already shows, so it announces as itself rather than as a second
  // unlabelled dialog over the one that opened it.
  ariaLabelledBy: 'prescription-form-title',
};

/** Which line is being authored, and on which guarantee. No id means a line is being added. */
export interface PrescriptionFormContext {
  guaranteeId: string;
  prescriptionId?: string;
  /** The line as it stands — {@link toPrescriptionWriteModel} of the stored record. */
  initial?: PrescriptionWriteModel;
  /** The guarantee's own diagnoses: the only ones a line may be pointed at. */
  diagnoses?: DiagnosisChipView[];
  /** When the guarantee was raised — the day whose package content the options are matched in. */
  guaranteeCreatedAt?: string;
  /** The package options the line is guaranteed under, as stored on it. */
  coverage?: PrescriptionPackageCoverageModel;
}

/**
 * Opens the form and carries its answer into the store. Held beside the form rather than in each
 * caller: adding a line from the guarantee's menu and amending one from the line's own menu must
 * dispatch the same way.
 *
 * The create-guarantee form does NOT come through here — it collects lines rather than writing
 * them, because the guarantee they belong to does not exist yet.
 */
export function openPrescriptionForm(dialog: MatDialog, store: Store, context: PrescriptionFormContext): void {
  const data: PrescriptionFormData = {
    mode: context.prescriptionId ? 'edit' : 'create',
    initial: context.initial,
    diagnoses: context.diagnoses,
    guaranteeCreatedAt: context.guaranteeCreatedAt,
    coverage: context.coverage,
    memberUserId: store.selectSignal(selectLiveRefreshContext)().memberUserId,
    // Only while writing a new line. A written one already carries a ruling, and moving it is the
    // ruling flow's act — it reads withdrawal rules this form does not.
    collectsDecision: !context.prescriptionId,
  };

  dialog
    .open<PrescriptionFormComponent, PrescriptionFormData, PrescriptionFormResult>(PrescriptionFormComponent, { ...PRESCRIPTION_FORM_DIALOG_CONFIG, data })
    .afterClosed()
    .pipe(take(1))
    .subscribe(result => {
      if (!result) {
        return;
      }

      const { guaranteeId, prescriptionId } = context;

      store.dispatch(
        prescriptionId
          ? // The form refuses to submit an amendment without one, so it is always stated by here.
            updatePrescription({ guaranteeId, prescriptionId, prescription: result.prescription, editReason: result.editReason ?? '', clearDiagnosis: result.clearDiagnosis })
          : // The ruling rides on the line: one write raises it ruled, whether one row or a course.
            addPrescription({
              guaranteeId,
              prescription: { ...result.prescription, decision: toDecisionWriteModel(result.decision) },
              clearDiagnosis: result.clearDiagnosis,
            })
      );
    });
}

/**
 * `Unknown` is left out: it is what an unmapped record reads back as, never something to author.
 * `PersonalDoctorAppointment` too — a visit to the member's own doctor is arranged through its own
 * flow, and a guarantee line naming one would be a second way to ask for something already booked.
 */
export const AUTHORABLE_KINDS: ServiceKind[] = [ServiceKind.Appointment, ServiceKind.InstrumentalDiagnostic, ServiceKind.LaboratoryTest, ServiceKind.Medication, ServiceKind.Other];

/** Every unit and every relation the enum states, in its order — neither list is curated. */
export const TIME_UNITS = Object.values(ScheduleTimeUnit);

export const MEAL_RELATIONS = Object.values(MealRelation);

const DAYS_IN_UNIT: Record<ScheduleTimeUnit, number> = {
  [ScheduleTimeUnit.Hour]: 1 / 24,
  [ScheduleTimeUnit.Day]: 1,
  [ScheduleTimeUnit.Week]: 7,
  [ScheduleTimeUnit.Month]: 30,
};

const DEFAULT_PERIOD_DAYS = 14;

/**
 * One form for every way a line is authored: added, amended, queued as a draft, free-form or coded.
 * Keep it one form — the gateway takes a single {@link PrescriptionWriteModel} for all of them.
 *
 * Coverage, planning and fulfilment have their own endpoints and survive an amendment untouched.
 * Everything else the model holds must be carried through: an update replaces what was authored.
 */
@Component({
  selector: 'guarantees-prescription-form',
  standalone: true,
  imports: [
    DecimalPipe,
    FormsModule,
    MatIcon,
    MatTooltip,
    TranslatePipe,
    ServicePickerComponent,
    OptionPickerComponent,
    ControlDirective,
    FieldLabelComponent,
    ToggleDirective,
    CheckBoxComponent,
    SpinnerComponent,
    ProviderRestrictionComponent,
    CategoryPickerComponent,
    PackageOptionPickerComponent,
  ],
  templateUrl: './prescription-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrescriptionFormComponent {
  /** Heading, scrolling body and pinned acts — the one dialog shape this module uses. */
  protected readonly layout = DIALOG_CLASSES;

  /** Holds a line under the control it belongs to, so a message never reflows the form. */
  protected readonly fieldMessage = FIELD_MESSAGE_CLASS;

  private readonly dialogRef = inject(MatDialogRef<PrescriptionFormComponent, PrescriptionFormResult>);

  protected readonly data = inject<PrescriptionFormData>(MAT_DIALOG_DATA);
  protected readonly isCreate = this.data.mode === 'create';
  /** A written line: it has a history, so the amendment states why it was made. */
  protected readonly isEdit = this.data.mode === 'edit';

  protected readonly submitLabelKey = this.isCreate
    ? 'guaranteeV2.action.addPrescription'
    : this.isEdit
      ? 'guaranteeV2.action.savePrescription'
      : 'guaranteeV2.action.saveDraftChanges';

  /**
   * The closed vocabulary an amendment is recorded under, shared with the V1 prescription forms
   * rather than restated: it is one policy, and two copies of it drift into two policies.
   */
  protected readonly editReasons: PickerOption[] = EDIT_REASONS.map(value => ({ value }));
  protected readonly editReason = signal('');

  protected readonly kindOptions: PickerOption[] = AUTHORABLE_KINDS.map(kind => ({
    value: kind,
    labelKey: SERVICE_KIND_LABEL_KEY[kind],
    icon: SERVICE_KIND_ICON[kind],
  }));

  protected readonly timeUnitOptions: PickerOption[] = TIME_UNITS.map(unit => ({ value: unit, labelKey: TIME_UNIT_LABEL_KEY[unit] }));
  protected readonly mealRelationOptions: PickerOption[] = MEAL_RELATIONS.map(relation => ({ value: relation, labelKey: MEAL_RELATION_LABEL_KEY[relation] }));

  private readonly initial = this.data.initial;
  private readonly initialDetails = this.initial?.details;
  private readonly initialSchedule = this.initialDetails && isDrugsWriteDetails(this.initialDetails) ? this.initialDetails.schedule : undefined;

  protected readonly kind = signal<ServiceKind>(this.initial?.kind ?? ServiceKind.Appointment);

  /**
   * «Інше» has no dictionary behind it — that is what makes it the kind for everything else — so a
   * line of that kind is always the manager's own words, and the choice is settled by the kind
   * rather than offered beside it.
   */
  /**
   * Only a visit is narrowed to a provider here. A laboratory order or a pharmacy basket is answered
   * by whoever holds the analysis or the drug, and pinning one before the line is even guaranteed
   * would be deciding on the member's behalf with none of the information the booking screen has.
   */
  protected readonly restrictable = computed(() => [ServiceKind.Appointment, ServiceKind.InstrumentalDiagnostic, ServiceKind.PersonalDoctorAppointment].includes(this.kind()));

  protected readonly restrictionService = computed(() => partnerServiceFor(this.kind()));

  protected readonly restriction = signal<PrescriptionRestrictionModel | undefined>(this.initial?.restriction);

  protected readonly freeFormLocked = computed(() => this.kind() === ServiceKind.Other);

  protected readonly isFreeForm = signal(this.kind() === ServiceKind.Other || (this.initial?.isFreeForm ?? false));

  protected readonly name = signal(this.initial?.name ?? '');
  protected readonly code = signal(this.initial?.code ?? '');

  /**
   * How many rows the line becomes: two massages are one order and two lines, each planned and
   * delivered on its own. Asked only of a line not yet written — an amendment replaces one row —
   * and never of a drug, whose amount is the schedule's own arithmetic.
   */
  protected readonly quantity = signal<number | null>(this.initialDetails?.quantity ?? 1);

  /** Partner integrations cannot express a quantity on a lab line, so it is fixed at one. */
  protected readonly isSingleUnitKind = computed(() => this.kind() === ServiceKind.LaboratoryTest);

  protected readonly startDate = signal(toDateInput(this.initialDetails?.startDate) || toDateInput(new Date().toISOString()));
  protected readonly endDate = signal(toDateInput(this.initialDetails?.endDate) || toDateInput(addDays(new Date(), DEFAULT_PERIOD_DAYS).toISOString()));

  // ── Medication only ─────────────────────────────────────────────────────────
  protected readonly form = signal(this.drugField('form'));
  protected readonly comment = signal(this.drugField('comment'));

  /** Empty until a drug is picked, amendments included: a stored line keeps codes, not products. */
  private readonly matchedDrugs = signal<DrugModel[]>([]);

  /** Kept until the service is picked again or typed over, so an untouched amendment keeps its products. */
  private readonly storedDrugCodes = signal<string[]>(this.initialDetails && isDrugsWriteDetails(this.initialDetails) ? (this.initialDetails.drugCodes ?? []) : []);

  /**
   * Only forms the dictionary sells: a typed spelling matches nothing when the pharmacy order is
   * built. Before a drug is picked, the line's own stated form is the only option.
   */
  protected readonly formOptions = computed(() => {
    const matched = uniqueForms(this.matchedDrugs());
    const stated = this.form().trim();

    return matched.length > 0 || !stated ? matched : [stated];
  });

  protected readonly formPickerOptions = computed<PickerOption[]>(() => this.formOptions().map(value => ({ value })));

  /** Narrowed by form — tablets must not be dispensed as syrup. Untouched until a drug is picked. */
  protected readonly soldDrugs = computed(() => {
    const matched = this.matchedDrugs();
    const form = this.form().trim().toLowerCase();

    return form ? matched.filter(drug => drug.formShort?.trim().toLowerCase() === form) : matched;
  });

  /** The products behind an ingredient pick, folded away: read when questioned, not by default. */
  protected readonly trademarksExpanded = signal(false);

  private readonly drugCodes = computed(() => {
    if (this.matchedDrugs().length === 0) {
      return this.storedDrugCodes();
    }

    return this.soldDrugs()
      .map(drug => drug.code)
      .filter((code): code is string => !!code);
  });

  /** What the dictionary says a package holds — the figure the packages/quantity pair is judged by. */
  protected readonly packageSizes = computed(() =>
    [...new Set(this.soldDrugs().map(drug => drug.quantityInPackage))].filter((size): size is number => size != null).sort((first, second) => first - second)
  );

  protected readonly dose = signal<number | null>(this.initialSchedule?.dose ?? null);
  protected readonly doseUnit = signal(this.initialSchedule?.doseUnit ?? '');
  protected readonly frequency = signal<number | null>(this.initialSchedule?.frequency ?? null);
  protected readonly period = signal<number | null>(this.initialSchedule?.period ?? 1);
  protected readonly periodUnit = signal<ScheduleTimeUnit>(this.initialSchedule?.periodUnit ?? ScheduleTimeUnit.Day);
  protected readonly duration = signal<number | null>(this.initialSchedule?.duration ?? null);
  protected readonly durationUnit = signal<ScheduleTimeUnit>(this.initialSchedule?.durationUnit ?? ScheduleTimeUnit.Day);
  protected readonly mealRelation = signal<MealRelation>(this.initialSchedule?.mealRelation ?? MealRelation.Independent);
  protected readonly asNeeded = signal(this.initialSchedule?.asNeeded ?? false);

  /** Folded away until asked for — except when the line already states one, which must stay visible. */
  protected readonly scheduleExpanded = signal(hasScheduleContent(this.initialSchedule));

  /** Seeded from the line: an amendment that omits financials clears reserved and franchise. */
  protected readonly reserved = signal<number | null>(this.initial?.financials?.reserved ?? null);
  protected readonly coverageFranchise = signal<number | null>(this.initial?.financials?.coverageFranchise ?? null);

  /**
   * Whether the reserve is somebody's answer rather than the baseline this form filled in. A line
   * being amended arrives with it already answered, and re-fetching it would quietly restate a
   * number the manager chose — so the baseline only ever fills a field nobody has written in.
   */
  private readonly reservedStated = signal(this.reserved() != null);

  private readonly baselines = inject(PrescriptionBaselinesService);
  private readonly destroyRef = inject(DestroyRef);

  /** Whether the member is on a flexible package — then a grant names the package option it is drawn against. */
  protected readonly flexiblePackage = toSignal(this.data.memberUserId ? inject(PackageCoverageMatchService).hasFlexibleCoverage(this.data.memberUserId) : of(false), {
    initialValue: false,
  });

  /** The member (by user id) whose package options are offered. */
  protected readonly memberUserId = this.data.memberUserId ?? '';

  /** The package option the line is drawn against — the stored one on an amendment, the queued one on a draft. */
  protected readonly packageSelection = signal<PackageOptionSelection | null>(selectionFromSnapshot(this.data.coverage) ?? selectionFromWriteModel(this.data.initial?.coverage));

  /** Changed here — an amendment sends the choice only then, since leaving it out keeps the stored one. */
  private readonly packageSelectionTouched = signal(false);

  /** What the line is filed under. Carried whole: the record stores the code beside the name. */
  protected readonly categories = signal<V2PrescriptionCategoryModel[]>(this.initial?.categories ?? []);

  /** The guarantee's own diagnoses: the only ones this line may be pointed at. */
  protected readonly diagnoses = this.data.diagnoses ?? [];

  protected readonly codingOf = codingOf;

  /**
   * A line authored from nothing adopts a sole diagnosis, which is what the record would give it
   * anyway. One that already exists is read exactly as it stands: it was offered the choice when it
   * was written, so carrying no diagnosis is an answer — somebody took it off — and seeding over it
   * here would be the form putting back what the reader had just removed.
   */
  protected readonly diagnosisId = signal(this.initial ? this.initial.diagnosisId : this.diagnoses.length === 1 ? this.diagnoses[0].id : undefined);

  protected readonly isMedication = computed(() => this.kind() === ServiceKind.Medication);

  /** A written line is one row and stays one: only a line yet to be raised can become a course. */
  protected readonly asksQuantity = computed(() => !this.isEdit && !this.isMedication());

  protected readonly isCoded = computed(() => !this.isFreeForm());

  /* ── The ruling, given in the same pass as the line it answers ────────────────────────────── */

  protected readonly segmentedGroup = SEGMENTED_GROUP_CLASS;
  protected readonly answers = DECISION_ANSWERS;
  protected readonly guaranteed = GuaranteeDecision.Guaranteed;
  protected readonly decisionIcon = DECISION_ICON;
  protected readonly decisionLabelKey = DECISION_LABEL_KEY;

  /** Asked only where the caller carries the answer somewhere. */
  protected readonly collectsDecision = !!this.data.collectsDecision;

  /**
   * Why cover could not be granted on this line as it is being authored — read from the fields on
   * screen, so choosing a diagnosis or re-picking the service clears it without leaving the dialog.
   *
   * The ruling flow's own {@link allowsGrant}, not a second reading of it: a form that allowed what
   * the flow refuses would be the way around the rule rather than a restatement of it.
   */
  protected readonly grantBlockerKey = computed(() => {
    const diagnosisId = this.diagnosisId();
    if (!allowsGrant({ kind: this.kind(), code: this.code(), isFreeForm: this.isFreeForm(), details: this.buildDetails(), diagnosisId })) {
      return DECISION_BLOCKER_HINT_KEY[diagnosisId ? DecisionBlocker.Unmapped : DecisionBlocker.NoDiagnosis];
    }

    return hasCategories({ categories: this.categories() }) ? '' : DECISION_BLOCKER_HINT_KEY[DecisionBlocker.NoCategories];
  });

  /**
   * The answer being given. A grant drops the moment the line stops supporting one — a diagnosis
   * taken back off, or a service re-authored as something unmapped — rather than travelling to a
   * write the service would refuse. A refusal is never disturbed: it needs neither of them.
   */
  protected readonly decision = linkedSignal<string, DecisionAnswer | undefined>({
    source: this.grantBlockerKey,
    computation: (blocker, previous) => {
      const current = previous ? previous.value : this.data.decision?.answer;

      return blocker && current === GuaranteeDecision.Guaranteed ? undefined : current;
    },
  });

  /** Grounds belong to the answer they were given under, so a changed answer asks for them again. */
  protected readonly decisionReason = linkedSignal<DecisionAnswer | undefined, string>({
    source: this.decision,
    computation: (answer, previous) => {
      if (!previous) {
        return this.data.decision?.reason ?? '';
      }

      return answer && previous.source === answer ? previous.value : '';
    },
  });

  /** One list per answer, built once: the picker reads its options by identity. */
  private readonly reasonsByAnswer: Record<DecisionAnswer, PickerOption[]> = {
    [GuaranteeDecision.Guaranteed]: GUARANTEE_REASONS.map(value => ({ value })),
    [GuaranteeDecision.NotGuaranteed]: DECLINE_REASONS.map(value => ({ value })),
  };

  protected readonly decisionReasonOptions = computed(() => {
    const answer = this.decision();

    return answer ? this.reasonsByAnswer[answer] : [];
  });

  /** Closed until an answer is given, where it names the condition rather than what to search. */
  protected readonly decisionReasonPlaceholderKey = computed(() => {
    const answer = this.decision();
    if (!answer) {
      return 'guaranteeV2.message.reasonNeedsDecision';
    }

    return answer === GuaranteeDecision.Guaranteed ? 'guaranteeV2.message.guaranteeReasonPlaceholder' : 'guaranteeV2.message.declineReasonPlaceholder';
  });

  /** A ruling nobody stated grounds for is one nobody can review, so it is not a finished answer. */
  protected readonly decisionReasonMissing = computed(() => this.collectsDecision && !!this.decision() && !this.decisionReason().trim());

  /** Unset is «без рішення» — one of the three, not the absence of a choice. */
  protected setDecision(answer?: DecisionAnswer): void {
    if (answer === GuaranteeDecision.Guaranteed && this.grantBlockerKey()) {
      return;
    }

    this.decision.set(answer);
  }

  /** Unmapped lines cannot be priced, limited or ordered. Re-picking the service is the fix. */
  protected readonly isUnmapped = computed(() => isPrescriptionUnmapped({ kind: this.kind(), code: this.code(), isFreeForm: this.isFreeForm(), details: this.buildDetails() }));

  /**
   * Whether to say so. Only once a service has actually been named: a line nobody has chosen one for
   * yet is unmapped by definition, and reporting it there tells a manager to pick a service again
   * when they have not picked one once — which is every newly opened form, before a word is typed.
   *
   * What is left is the case the note exists for: a service was chosen and still matched nothing the
   * engine can act on — a drug with no trademarks behind it, or a code from another dictionary. The
   * empty field has its own answer already, in the required message under the picker.
   *
   * A free-form line never reaches here. It is uncoded deliberately, which is not the same fact.
   */
  protected readonly showsUnmapped = computed(() => !this.serviceMissing() && this.isUnmapped());

  /** Preview only — the server recomputes both from the schedule, and its figures are the record. */
  protected readonly dailyDose = computed(() => {
    const dose = this.dose();
    const frequency = this.frequency();
    const period = this.period();
    if (!dose || !frequency || !period) {
      return null;
    }

    return round(dose * (frequency / (period * DAYS_IN_UNIT[this.periodUnit()])));
  });

  protected readonly courseDose = computed(() => {
    const daily = this.dailyDose();
    const duration = this.duration();

    return daily && duration ? round(daily * duration * DAYS_IN_UNIT[this.durationUnit()]) : null;
  });

  // ── How much of it ──────────────────────────────────────────────────────────

  private readonly initialQuantity = this.drugNumber('requiredQuantity');

  /**
   * Whether the units were stated by hand. Until they are, the field follows the schedule: the
   * course dose IS what the whole course comes to, so asking the manager to copy it across would be
   * asking twice for one answer. Emptying the field hands it back to the schedule.
   */
  private readonly quantityStated = signal(this.initialQuantity != null);

  /** Units the whole course comes to: what the manager stated, or what the schedule works out to. */
  protected readonly requiredQuantity = linkedSignal<{ derived: number | null; stated: boolean }, number | null>({
    source: () => ({ derived: this.courseDose(), stated: this.quantityStated() }),
    computation: ({ derived, stated }, previous) => (stated ? (previous?.value ?? this.initialQuantity) : derived),
  });

  protected readonly treatmentPackages = signal<number | null>(this.drugNumber('treatmentPackages'));

  /**
   * Which of the two amounts the pharmacy order will actually be placed for. The packages win
   * wherever they are stated — including on a line that already carried both — so the other field is
   * closed rather than left open to state a figure nothing would read. A package count holds for
   * every product behind the line: the pharmacy sells that many boxes of whichever one is dispensed.
   */
  protected readonly governingAmount = computed<'packages' | 'quantity' | null>(() => {
    if (this.treatmentPackages()) {
      return 'packages';
    }

    return this.requiredQuantity() ? 'quantity' : null;
  });

  /** How many boxes of THIS product the stated units come to. Rounded up: pharmacies sell whole ones. */
  protected packagesFor(drug: DrugModel): number | null {
    const required = this.requiredQuantity();

    return required && drug.quantityInPackage ? Math.ceil(required / drug.quantityInPackage) : null;
  }

  protected setRequiredQuantity(value: number | null): void {
    this.quantityStated.set(value != null);
    this.requiredQuantity.set(value);
  }

  /** Money, to the kopiyka: a price list answers with more places than a sum can be paid in. */
  private readonly reservedAmount = computed(() => {
    const reserved = this.reserved();

    return reserved == null ? undefined : round(reserved);
  });

  protected setReserved(value: number | null): void {
    this.reservedStated.set(value != null);
    this.reserved.set(value);
  }

  protected setCoverageFranchise(value: number | null): void {
    this.coverageFranchise.set(value);
  }

  /** The baseline says while it is being read, so an empty field is not read as the answer. */
  protected readonly reservedLoading = signal(false);

  /** What the offering is listed at, read once the service is picked — its code is the only key. */
  private loadReserved(): void {
    const code = this.code().trim();
    if (!code || this.reservedStated()) {
      return;
    }

    this.reservedLoading.set(true);
    this.baselines
      .maxPrice(code)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: price => {
          if (price != null && !this.reservedStated()) {
            this.reserved.set(round(price));
          }
          this.reservedLoading.set(false);
        },
        error: () => this.reservedLoading.set(false),
      });
  }

  /**
   * What the line is for, still unanswered. A coded line is named by the dictionary entry behind it;
   * a free-form one by the words typed into it, so which field is owed depends on the mode.
   */
  protected readonly serviceMissing = computed(() => !(this.isCoded() ? this.code().trim() : this.name().trim()));

  /** The ICD-10 code of the chosen diagnosis — what the package options' diagnoses are matched against. */
  protected readonly diagnosisCode = computed(() => this.diagnoses.find(diagnosis => diagnosis.id === this.diagnosisId())?.icd10?.code);

  /** Cover is being given with this write: a new line ruled a grant, or an amendment of a guaranteed one. */
  protected readonly grants = computed(() =>
    this.collectsDecision ? this.decision() === GuaranteeDecision.Guaranteed : this.initial?.decision?.decision === GuaranteeDecision.Guaranteed
  );

  /** Options are matched against the categories, so none are offered before the line is categorised. */
  protected readonly hasCategories = computed(() => this.categories().length > 0);

  /** The choice as it counts: one kept after every category was taken back off is no longer offered, so it is not sent. */
  private readonly chosenPackageOption = computed(() => (this.hasCategories() ? this.packageSelection() : null));

  /**
   * Where the package options are offered: on a flexible package, once the line is categorised, and only
   * where this form can grant — while authoring with a ruling, or amending a line already guaranteed.
   */
  protected readonly offersPackageOption = computed(() => this.flexiblePackage() && this.hasCategories() && (this.collectsDecision || this.grants()));

  /** On a flexible package a grant is written with the option it is drawn against. */
  protected readonly asksPackageOption = computed(() => this.flexiblePackage() && this.grants());

  protected readonly packageOptionsStatus = signal<PackageOptionsStatus>('loading');

  /** A grant on a flexible package names its option, unless the package has none to choose from. */
  protected readonly packageOptionMissing = computed(
    () => this.offersPackageOption() && this.asksPackageOption() && this.packageOptionsStatus() !== 'none' && !this.chosenPackageOption()
  );

  /** Every line is filed under at least one category: package options and coverage are matched on them. */
  protected readonly categoriesMissing = computed(() => !this.hasCategories());

  /** Never optional: an amendment nobody stated a ground for is one nobody can review afterwards. */
  protected readonly editReasonMissing = computed(() => this.isEdit && !this.editReason().trim());

  protected readonly submitBlockKey = computed(() => {
    if (this.serviceMissing()) {
      return 'guaranteeV2.message.prescriptionNeedsService';
    }

    if (this.decisionReasonMissing()) {
      return 'guaranteeV2.message.decisionReasonRequired';
    }

    if (this.categoriesMissing()) {
      return 'guaranteeV2.message.processCategoriesRequired';
    }

    if (this.packageOptionMissing()) {
      return 'guaranteeV2.message.processOptionRequired';
    }

    return this.editReasonMissing() ? 'guaranteeV2.message.editReasonRequired' : '';
  });

  protected readonly canSubmit = computed(() => !this.submitBlockKey());

  /** Clicking the chosen diagnosis detaches the line from it. */
  protected setDiagnosis(id: string): void {
    this.diagnosisId.update(current => (current === id ? undefined : id));
  }

  /** A new kind is a new dictionary and a new shape: everything the old one filled in is void. */
  protected setKind(value: string): void {
    const kind = value as ServiceKind;
    if (kind === this.kind()) {
      return;
    }

    this.kind.set(kind);
    if (this.freeFormLocked()) {
      this.isFreeForm.set(true);
    }
    this.code.set('');
    this.name.set('');
    this.quantity.set(1);
    this.detachFromDictionary();
    this.quantityStated.set(false);
    this.treatmentPackages.set(null);
    this.comment.set('');
    this.dose.set(null);
    this.doseUnit.set('');
    this.frequency.set(null);
    this.period.set(1);
    this.periodUnit.set(ScheduleTimeUnit.Day);
    this.duration.set(null);
    this.durationUnit.set(ScheduleTimeUnit.Day);
    this.mealRelation.set(MealRelation.Independent);
    this.asNeeded.set(false);
    this.scheduleExpanded.set(false);
  }

  /**
   * Typing over the pick departs from the dictionary — a hand-stated service must not carry the
   * products of the one it replaced.
   */
  protected editName(value: string): void {
    this.name.set(value);
    this.detachFromDictionary();
  }

  protected editCode(value: string): void {
    this.code.set(value);
    this.detachFromDictionary();
  }

  private detachFromDictionary(): void {
    // The form is only the old match's where a match stood — a hand-typed one is the manager's own.
    if (this.matchedDrugs().length > 0) {
      this.form.set('');
    }
    this.matchedDrugs.set([]);
    this.storedDrugCodes.set([]);
  }

  protected setPeriodUnit(value: string): void {
    this.periodUnit.set(value as ScheduleTimeUnit);
  }

  protected setDurationUnit(value: string): void {
    this.durationUnit.set(value as ScheduleTimeUnit);
  }

  protected setMealRelation(value: string): void {
    this.mealRelation.set(value as MealRelation);
  }

  protected toggleFreeForm(): void {
    if (this.freeFormLocked()) {
      return;
    }

    this.isFreeForm.update(value => !value);
  }

  /** Replaces rather than adds: products from a previous pick belong to a different drug. */
  protected pickService(selection: ServiceSelection): void {
    this.code.set(selection.coding.code ?? '');
    this.name.set(selection.coding.name ?? '');

    if (this.isMedication()) {
      this.matchedDrugs.set(selection.drugs ?? []);
      this.storedDrugCodes.set([]);
      const forms = uniqueForms(selection.drugs ?? []);
      this.form.set(forms.length === 1 ? forms[0] : '');
      // A package count belongs to the product it was counted for, and this is a different one.
      this.treatmentPackages.set(null);
    }

    this.loadReserved();
  }

  protected submit(): void {
    if (!this.canSubmit()) {
      return;
    }

    const answer = this.decision();

    this.dialogRef.close({
      editReason: this.isEdit ? this.editReason().trim() : undefined,
      decision: this.collectsDecision && answer ? { answer, reason: this.decisionReason().trim() } : undefined,
      // Only where diagnoses were on offer: a guarantee carrying none has nothing to detach from,
      // and the call would be a round trip that says nothing.
      clearDiagnosis: this.diagnoses.length > 0 && !this.diagnosisId(),
      prescription: {
        kind: this.kind(),
        code: this.code().trim(),
        name: this.name().trim(),
        isFreeForm: this.isFreeForm(),
        diagnosisId: this.diagnosisId(),
        categories: this.categories(),
        // Not asked for here, and an amendment clears what it does not state — so carry them through.
        completedInAppointment: this.initial?.completedInAppointment,
        completionReason: this.initial?.completionReason,
        // Restated rather than re-answered: the service refuses an amendment that states no ruling.
        decision: this.initial?.decision,
        details: this.buildDetails(),
        // Dropped outright where the kind stopped being one that is narrowed: a laboratory line
        // carrying a doctor from when it was a visit would narrow a search that never asks about one.
        restriction: this.restrictable() ? this.restriction() : undefined,
        financials: {
          reserved: this.reservedAmount(),
          coverageFranchise: this.coverageFranchise() ?? undefined,
        },
        coverage: this.coverageToWrite(),
      },
    });
  }

  /** The franchise a ticked sub-option states fills the field; the manager may still change it. */
  protected fillFranchiseFrom(subOption: PackageCoverageMatchSubOptionModel): void {
    const franchise = franchiseLimit(subOption.limits);
    if (franchise != null) {
      this.coverageFranchise.set(franchise);
    }
  }

  protected setPackageSelection(selection: PackageOptionSelection | null): void {
    this.packageSelection.set(selection);
  }

  protected touchPackageSelection(): void {
    this.packageSelectionTouched.set(true);
  }

  /** The option block as the line is written: with a grant only, and on an amendment only when changed here. */
  private coverageToWrite(): PrescriptionPackageCoverageWriteModel | undefined {
    const selection = this.chosenPackageOption();
    if (!this.asksPackageOption() || !selection || (!this.collectsDecision && !this.packageSelectionTouched())) {
      return undefined;
    }

    return toCoverageWriteModel(selection);
  }

  protected cancel(): void {
    this.dialogRef.close();
  }

  constructor() {
    // Asked on opening as well as on picking: a line amended after being authored without a figure
    // has a code to price and a blank to fill, and only the pick would ever have filled it.
    this.loadReserved();
  }

  /**
   * The kind decides the `_dt` family, so changing it must change the shape on the wire.
   * `_dt` is written first — the backend's polymorphic reader only accepts a leading discriminator.
   */
  private buildDetails(): PrescriptionDetailsWriteModel {
    const base = {
      // Where it is not asked, carried from the stored line rather than fixed at one: an amendment
      // replaces the details whole, and a line that stated more would lose it here.
      quantity: this.asksQuantity() ? Math.max(1, Math.floor(this.quantity() || 1)) : (this.initialDetails?.quantity ?? 1),
      startDate: startOfDayIso(this.startDate()),
      endDate: endOfDayIso(this.endDate()),
    };

    if (this.kind() === ServiceKind.LaboratoryTest) {
      return { _dt: PrescriptionDetailsKind.Laboratory, ...base };
    }

    if (!this.isMedication()) {
      return { _dt: PrescriptionDetailsKind.Appointment, ...base };
    }

    const previous = this.initialDetails && isDrugsWriteDetails(this.initialDetails) ? this.initialDetails : undefined;

    return {
      _dt: PrescriptionDetailsKind.Drugs,
      ...base,
      // The protocol belongs to the service, so an amendment keeps it rather than clearing it.
      protocolIdentifier: previous?.protocolIdentifier,
      drugCodes: this.drugCodes().length > 0 ? this.drugCodes() : undefined,
      form: this.form().trim() || undefined,
      // Both are sent where both are held: the packages are what is ordered, the units what was
      // written, and dropping either would leave the record unable to say which figure it is.
      requiredQuantity: this.requiredQuantity() ?? undefined,
      // Never sent while more than one product stands behind the line: the boxes differ, so the
      // count would name an amount nobody could dispense.
      treatmentPackages: this.treatmentPackages() ?? undefined,
      comment: this.comment().trim() || undefined,
      schedule: this.buildSchedule(),
    };
  }

  /** Never send `dailyDose`/`courseDose`: the service derives both from these fields. */
  private buildSchedule(): DrugScheduleWriteModel {
    return {
      dose: this.dose() ?? undefined,
      doseUnit: this.doseUnit().trim() || undefined,
      frequency: this.frequency() ?? undefined,
      period: this.period() ?? undefined,
      periodUnit: this.periodUnit(),
      duration: this.duration() ?? undefined,
      durationUnit: this.durationUnit(),
      mealRelation: this.mealRelation(),
      timesOfDay: this.initialSchedule?.timesOfDay,
      asNeeded: this.asNeeded(),
    };
  }

  private drugField(field: 'form' | 'comment'): string {
    const details = this.initialDetails;

    return details && isDrugsWriteDetails(details) ? (details[field] ?? '') : '';
  }

  private drugNumber(field: 'requiredQuantity' | 'treatmentPackages'): number | null {
    const details = this.initialDetails;

    return details && isDrugsWriteDetails(details) ? (details[field] ?? null) : null;
  }
}

function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);

  return result;
}

function round(value: number): number {
  return Math.round(value * 100) / 100;
}

/** Case-blind, first spelling kept: the dictionary states the same form more than one way. */
function uniqueForms(drugs: DrugModel[]): string[] {
  const seen = new Map<string, string>();
  for (const drug of drugs) {
    const form = drug.formShort?.trim();
    if (form && !seen.has(form.toLowerCase())) {
      seen.set(form.toLowerCase(), form);
    }
  }

  return [...seen.values()];
}

function hasScheduleContent(schedule: DrugScheduleWriteModel | undefined): boolean {
  return !!schedule && (schedule.dose != null || schedule.frequency != null || schedule.duration != null || schedule.asNeeded);
}
